import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Stack,
  Typography,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Skeleton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { FilterList, Refresh, Download } from '@mui/icons-material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import api from '../../services/api';
const apiUrl = process.env.REACT_APP_API_URL;

export default function VetCheckupReport() {
  const { t } = useTranslation();
  const severityMeta = useMemo(
    () => ({
      critical: {
        rank: 4,
        color: 'error',
        label: t('vetCheckup.critical', 'حرجة'),
      },
      high: { rank: 3, color: 'warning', label: t('vetCheckup.high', 'عالية') },
      medium: {
        rank: 2,
        color: 'info',
        label: t('vetCheckup.medium', 'متوسطة'),
      },
      low: { rank: 1, color: 'default', label: t('vetCheckup.low', 'منخفضة') },
    }),
    [t]
  );

  const worstSeverityOf = (items = []) => {
    let best = null;
    for (const it of items) {
      const key = String(it?.severity || '').toLowerCase();
      const s = severityMeta[key];
      if (s && (!best || s.rank > best.rank)) best = s;
    }
    return best; // قد تكون null إذا ما فيه بيانات
  };

  const anyActionRequired = (items = []) =>
    items.some((it) => Number(it?.action_required));

  const viewSessionIdRef = useRef(uuidv4());
  const logAudit = useCallback(async (payload) => {
    try {
      await axios.post(`${apiUrl}/audit/log`, {
        view_session_id: viewSessionIdRef.current,
        user_id: null, // أو من الـ context/session لو متوفر
        role_id: null,
        ...payload, // <-- هذه هي المهمة
      });
    } catch (_) {
      /* لا تمنع الواجهة */
    }
  }, []);

  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: '',
    sev: 'success',
  });
  const showSnack = useCallback((msg, sev = 'success') => {
    setSnackbar({ open: true, msg, sev });
  }, []);

  // يجمع أسماء الخيل من عناصر الجولة مع إزالة التكرار
  const getHorseLabel = useCallback((items) => {
    if (!Array.isArray(items) || !items.length) return '';
    const uniq = [];
    for (const it of items) {
      const name =
        it.horse_name ||
        it.horse_name_ar ||
        it.horse_name_en ||
        it.horse || // في حال أرسلت من الـ API بهذا المفتاح
        it.horse_id;
      if (name != null && !uniq.includes(name)) uniq.push(String(name));
    }
    const firstPart = uniq.slice(0, 3).join('، ');
    return firstPart + (uniq.length > 3 ? ` +${uniq.length - 3}` : '');
  }, []);

  /*const buildWhatsAppText = useCallback(
    (round, items) => {
      const horses = getHorseLabel(items);
      const procedures = items
        .map(
          (it) =>
            it.type_name_ar ||
            it.procedure_name_ar ||
            it.type_name_en ||
            it.procedure_name_en ||
            ''
        )
        .filter(Boolean)
        .join('، ');
      const notes = items.find((i) => i.notes)?.notes || '';
      const date = new Date().toISOString().slice(0, 10);

      return `تنبيه: تم تنفيذ الإجراءات التالية على ${
        horses || 'الخيل'
      } بتاريخ ${date}.
${procedures || '—'}${notes ? `\nملاحظات: ${notes}` : ''}`;
    },
    [getHorseLabel]
  );
*/
  // ---------- Helpers ----------
  const formatDateTime = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    // تنسيق بالتاريخ والوقت وفق منطقة الرياض
    return new Intl.DateTimeFormat('ar-SA', {
      timeZone: 'Asia/Riyadh',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  };
  const [tplKey, setTplKey] = useState('PROCEDURE_PERFORMED'); // الافتراضي

  const previewOwnerAlert = useCallback(
    async ({ horseId, round, items }) => {
      const payload = {
        service_type: 'vetclinic',
        horse_id: Number(horseId),
        horse_origin: round?.horse_origin || 'Inside',
        language: 'ar',
        template_key: tplKey, // يبقى كما هو إذا مُعرّف أعلى الكمبوننت
        items: (items || []).map((it) => ({
          procedure_name_ar: it.type_name_ar || it.procedure_name_ar || 'إجراء',
          procedure_name_en:
            it.type_name_en || it.procedure_name_en || 'Procedure',
          severity: it.severity || 'low',
          action_required: Number(it.action_required) ? 1 : 0,
          notes: it.notes || '',
        })),
      };

      const r = await api.post('/Alerts/owner-alerts/preview', payload);
      return r?.data?.body_rendered ?? '';
    },
    [tplKey]
  );

  // يبني نص واتساب من عناصر الجولة (نص بسيط وواضح)
  /*const buildWhatsAppText = (round, items) => {
    // أسماء الخيل (موجود عندك helper جاهز getHorseLabel)
    const horses = getHorseLabel(items);

    // الإجراءات (عربي أولاً ثم إنجليزي لو مفقود)
    const procedures = items
      .map(
        (it) =>
          it.type_name_ar ||
          it.procedure_name_ar ||
          it.type_name_en ||
          it.procedure_name_en ||
          ''
      )
      .filter(Boolean)
      .join('، ');

    const notes = items.find((i) => i.notes)?.notes || '';
    const date = new Date().toISOString().slice(0, 10);

    return `تنبيه: تم تنفيذ الإجراءات التالية على ${
      horses || 'الخيل'
    } بتاريخ ${date}.
${procedures || '—'}${notes ? `\nملاحظات: ${notes}` : ''}`;
  };*/
  // يبني نص واتساب من عناصر الجولة (نص بسيط وواضح)
  const buildWhatsAppText = useCallback(
    (round, items) => {
      // أسماء الخيل (موجود عندك helper جاهز getHorseLabel)
      const horses = getHorseLabel(items);

      // الإجراءات (عربي أولاً ثم إنجليزي لو مفقود)
      const procedures = items
        .map(
          (it) =>
            it.type_name_ar ||
            it.procedure_name_ar ||
            it.type_name_en ||
            it.procedure_name_en ||
            ''
        )
        .filter(Boolean)
        .join('، ');

      const notes = items.find((i) => i.notes)?.notes || '';
      const date = new Date().toISOString().slice(0, 10);

      return `تنبيه: تم تنفيذ الإجراءات التالية على ${
        horses || 'الخيل'
      } بتاريخ ${date}.
${procedures || '—'}${notes ? `\nملاحظات: ${notes}` : ''}`;
    },
    [getHorseLabel]
  );

  const sendWhatsAppForRound = useCallback(
    async (round) => {
      try {
        const list = Array.isArray(round.items) ? round.items : [];
        if (!list.length) {
          showSnack('لا توجد عناصر في هذه الجولة', 'error');
          return;
        }

        // نجمع العناصر حسب horse_id (رسالة مستقلة لكل حصان/مالك)
        const groups = list.reduce((acc, it) => {
          const hid = it.horse_id || round.horse_id;
          if (!hid) return acc;
          (acc[hid] ||= []).push(it);
          return acc;
        }, {});

        let opened = 0;

        for (const [horseId, items] of Object.entries(groups)) {
          // 1) رقم المالك من الخادم (كما تفعل الآن)
          const r = await axios.get(
            `${apiUrl}/Alerts/owner-alerts/recipient/${horseId}`
          );
          const phone = r?.data?.owner_mobile;
          if (!phone) {
            showSnack('لا يوجد رقم جوال للمالك', 'warning');
            continue;
          }

          // 2) النص الموحّد من الخادم (نفس القالب المستخدم للإيميل)
          let text = '';
          try {
            text = await previewOwnerAlert({ horseId, round, items });
          } catch {
            // Fallback (اختياري): لو فشل الـpreview لأي سبب
            text = buildWhatsAppText(round, items);
          }

          // 3) افتح wa.me بنفس النص الموحّد
          window.open(waLink(phone, text), '_blank', 'noopener,noreferrer');
          opened++;
        }

        if (opened) showSnack('تم فتح واتساب لإرسال الرسائل');
        else showSnack('تعذر فتح أي رسالة واتساب', 'warning');
      } catch (e) {
        console.error(e);
        showSnack('تعذر فتح واتساب', 'error');
      }
    },
    [showSnack, buildWhatsAppText, previewOwnerAlert]
  );

  // يجمع أسماء الخيل من عناصر الجولة مع إزالة التكرار

  // ---------- Filters ----------
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [stableId, setStableId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [vetId, setVetId] = useState('');
  const [q, setQ] = useState('');
  const [sending, setSending] = useState(new Set());
  const [tpls, setTpls] = useState([]);

  // ---------- Datasets / Dropdowns ----------
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);

  const [vets, setVets] = useState([]);
  const [loadingVets, setLoadingVets] = useState(false);

  // ---------- Report Data ----------
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // ---------- Abort Controllers ----------
  const roomsAbortRef = useRef(null);
  const vetsAbortRef = useRef(null);
  const roundsAbortRef = useRef(null);
  // Builds a wa.me link (phone must be in full international format e.g., 9665XXXXXXXX)
  // يبني رابط wa.me (الموبايل بصيغة دولية بدون +، والنص URL-encoded)
  const waLink = (phone, text) => {
    const digits = String(phone).replace(/\D/g, '');
    return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
  };

  // ---------- Derived: Stables & Rooms ----------
  const stables = useMemo(() => {
    const map = new Map();
    for (const r of rooms) {
      if (!map.has(r.stable_id)) map.set(r.stable_id, r.stable_name);
    }
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), 'ar', {
        sensitivity: 'base',
      })
    );
  }, [rooms]);

  const roomsOfSelectedStable = useMemo(() => {
    if (!stableId) return [];
    const list = rooms.filter((r) => r.stable_id === Number(stableId));
    const map = new Map();
    for (const r of list)
      if (!map.has(r.room_id)) map.set(r.room_id, r.room_name);
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), 'ar', { numeric: true })
    );
  }, [rooms, stableId]);

  // ---------- Pagination ----------
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const pagedRounds = useMemo(
    () => rounds.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rounds, page, rowsPerPage]
  );

  // ---------- Items Dialog ----------
  const [itemsOfRound, setItemsOfRound] = useState(null); // null أو { round, items }

  // ---------- Loaders ----------
  const loadRooms = useCallback(async () => {
    setLoadingRooms(true);
    try {
      roomsAbortRef.current?.abort();
      roomsAbortRef.current = new AbortController();
      const r = await axios.get(`${apiUrl}/rooms/rooms-with-stable-count`, {
        signal: roomsAbortRef.current.signal,
      });
      setRooms(Array.isArray(r.data) ? r.data : []);
    } catch {
      setRooms([]); // لا توقف الصفحة لو فشل
    } finally {
      setLoadingRooms(false);
    }
  }, []);

  const loadVets = useCallback(async () => {
    setLoadingVets(true);
    try {
      vetsAbortRef.current?.abort();
      vetsAbortRef.current = new AbortController();

      // نحاول عدّة مسارات، ونتجاهل 404 بهدوء
      const tryEndpoints = [
        `${apiUrl}/users?role=Veterinarian`,
        `${apiUrl}/users?role=vet`,
        `${apiUrl}/vet-clinic/vets`,
      ];
      for (const url of tryEndpoints) {
        try {
          const r = await axios.get(url, {
            signal: vetsAbortRef.current.signal,
          });
          const arr = Array.isArray(r.data) ? r.data : [];
          if (arr.length) {
            const norm = arr
              .map((v) => ({
                id: v.user_id || v.vet_id || v.id,
                name: v.username || v.name || v.full_name,
              }))
              .filter((v) => v.id);
            setVets(norm);
            return;
          }
        } catch {
          // ignore
        }
      }
      setVets([]);
    } finally {
      setLoadingVets(false);
    }
  }, []);

  const loadRounds = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      roundsAbortRef.current?.abort();
      roundsAbortRef.current = new AbortController();
      const params = {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        stable_id: stableId || undefined,
        room_id: roomId || undefined,
        vet_user_id: vetId || undefined,
        q: q || undefined,
      };
      const r = await axios.get(`${apiUrl}/checkup/rounds`, {
        params,
        signal: roundsAbortRef.current.signal,
      });

      // تأكد دائمًا من أن items مصفوفة (ولو كانت JSON نصًا يتم تحويلها)
      const arr = Array.isArray(r.data) ? r.data : [];
      const normalized = arr.map((row) => {
        let items = row.items;
        if (typeof items === 'string') {
          try {
            items = JSON.parse(items);
          } catch {
            items = [];
          }
        }
        if (!Array.isArray(items)) items = [];
        return { ...row, items };
      });
      setRounds(normalized);
      setPage(0); // ارجع للصفحة الأولى بعد أي تحميل جديد
    } catch (e) {
      if (!axios.isCancel(e)) {
        setErr(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            (e?.response?.status >= 500
              ? t('common.serverError', 'خطأ من الخادم')
              : t('vetReport.loadError', 'فشل تحميل التقرير'))
        );
      }
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, stableId, roomId, vetId, q, t]);

  // ---------- Init / Effects ----------
  useEffect(() => {
    loadRooms();
    loadVets();
    return () => {
      roomsAbortRef.current?.abort();
      vetsAbortRef.current?.abort();
      roundsAbortRef.current?.abort();
    };
  }, [loadRooms, loadVets]);
  useEffect(() => {
    logAudit({
      entity_type: 'vet_checkup_report',
      entity_id: 0,
      action: 'VIEW',
    });
  }, [logAudit]);

  useEffect(() => {
    loadRounds();
  }, [loadRounds]);

  // ---------- UI: Snackbar ----------

  const handleExportJSON = () => {
    logAudit({
      entity_type: 'vet_checkup_report',
      entity_id: 0,
      action: 'EXPORT',
    });
    try {
      const blob = new Blob([JSON.stringify(rounds, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vet-checkup-report_${dateFrom}_to_${dateTo}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showSnack(t('common.exported', 'تم التصدير'));
    } catch {
      showSnack(t('common.exportFailed', 'فشل التصدير'), 'error');
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${apiUrl}/Alerts/templates`, {
          params: { q: '' },
        });
        const arr = Array.isArray(r.data) ? r.data : [];
        // فلترة اختيارية: قوالب تدعم القنوات التي تحتاجها
        setTpls(
          arr.filter(
            (x) =>
              String(x.channels_csv || '').includes('Email') ||
              String(x.channels_csv || '').includes('WhatsApp')
          )
        );
      } catch {}
    })();
  }, []);

  /*const notifyOwnersForRoundWhatsApp = useCallback(
    async (round) => {
      const rowKey = String(round.round_id ?? round.id);
      if (sending.has(rowKey)) return;
      setSending((prev) => new Set(prev).add(rowKey));

      try {
        const token = localStorage.getItem('token');
        const itemsList = Array.isArray(round.items) ? round.items : [];
        if (!itemsList.length) {
          showSnack('لا توجد عناصر في هذه الجولة', 'error');
          return;
        }

        const groups = itemsList.reduce((acc, it) => {
          const hid = it.horse_id || round.horse_id;
          if (!hid) return acc;
          (acc[hid] ||= []).push(it);
          return acc;
        }, {});

        let ok = 0,
          fail = 0,
          skipped = 0,
          dupFound = 0;
        const sendOne = async (horseId, items, force = false) => {
          const payload = {
            service_type: 'vetclinic',
            horse_id: Number(horseId),
            horse_origin: round.horse_origin || 'Inside',
            language: 'ar',
            template_key: tplKey,
            items: items.map((it) => ({
              procedure_name_ar:
                it.type_name_ar || it.procedure_name_ar || 'إجراء',
              procedure_name_en:
                it.type_name_en || it.procedure_name_en || 'Procedure',
              severity: it.severity || 'low',
              action_required: Number(it.action_required) ? 1 : 0,
              notes: it.notes || '',
            })),
            override_channels: ['InApp', 'WhatsApp'], // ← أهم سطر
            ...(force ? { force_resend: true } : {}),
            created_by: null,
          };
          const headers = {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'X-Idempotency-Key': `round-${rowKey}-horse-${horseId}-${
              force ? 'force' : 'normal'
            }`,
          };
          return axios.post(`${apiUrl}/Alerts/owner-alerts/notify`, payload, {
            headers,
          });
        };

        for (const [horseId, items] of Object.entries(groups)) {
          try {
            await sendOne(horseId, items, false);
            ok++;
          } catch (err) {
            const status = err?.response?.status,
              data = err?.response?.data;
            if (status === 409 && data?.duplicate_today) {
              dupFound++;
              if (
                window.confirm(
                  'تم إرسال إشعار لهذا الخيل اليوم. هل تريد إعادة الإرسال؟'
                )
              ) {
                try {
                  await sendOne(horseId, items, true);
                  ok++;
                } catch {
                  fail++;
                }
              } else {
                skipped++;
              }
            } else {
              fail++;
            }
          }
        }

        if (ok && !fail)
          showSnack(
            `تم الإرسال بنجاح إلى ${ok} خيل${
              skipped ? `، وتخطّينا ${skipped}` : ''
            }`
          );
        else if (ok && fail)
          showSnack(
            `أُرسل ${ok} وفشل ${fail}${skipped ? ` وتخطّينا ${skipped}` : ''}`,
            'warning'
          );
        else showSnack('فشل الإرسال', 'error');
      } finally {
        setSending((prev) => {
          const next = new Set(prev);
          next.delete(String(rowKey));
          return next;
        });
      }
    },
    [showSnack, sending, setSending]
  );*/

  const notifyOwnersForRound = useCallback(
    async (round) => {
      // مفتاح فريد للصف (يُستخدم لتعطيل الزر مؤقتًا ومنع النقرات المتكررة)
      const rowKey = String(round.round_id ?? round.id);

      // إن كانت هذه الجولة قيد الإرسال بالفعل — تجاهل النقرات المتكررة
      if (sending.has(rowKey)) return;

      // علّم الجولة بأنها قيد الإرسال (للتعطيل المؤقت)
      setSending((prev) => {
        const next = new Set(prev);
        next.add(rowKey);
        return next;
      });

      try {
        const token = localStorage.getItem('token');
        const itemsList = Array.isArray(round.items) ? round.items : [];
        if (!itemsList.length) {
          showSnack('لا توجد عناصر في هذه الجولة', 'error');
          return;
        }

        // نجمع العناصر حسب horse_id (إشعار مستقل لكل حصان)
        const groups = itemsList.reduce((acc, it) => {
          const hid = it.horse_id || round.horse_id;
          if (!hid) return acc;
          (acc[hid] ||= []).push(it);
          return acc;
        }, {});

        let ok = 0,
          fail = 0,
          skipped = 0,
          dupFound = 0;

        const formatKSA = (iso) => {
          try {
            return new Intl.DateTimeFormat('ar-SA', {
              timeZone: 'Asia/Riyadh',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            }).format(new Date(iso));
          } catch {
            return '';
          }
        };

        // مُرسل واحد لكل حصان (مع خيار فرض إعادة الإرسال)
        const sendOne = async (horseId, items, force = false) => {
          const payload = {
            service_type: 'vetclinic',
            horse_id: Number(horseId),
            horse_origin: round.horse_origin || 'Inside',
            language: 'ar',
            template_key: tplKey,
            items: items.map((it) => ({
              procedure_name_ar:
                it.type_name_ar || it.procedure_name_ar || 'إجراء',
              procedure_name_en:
                it.type_name_en || it.procedure_name_en || 'Procedure',
              severity: it.severity || 'low',
              action_required: Number(it.action_required) ? 1 : 0,
              notes: it.notes || '',
            })),
            ...(force ? { force_resend: true } : {}),
            created_by: null,
          };

          const headers = {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            // مفتاح idempotency اختياري — يفيد إن كان الباك يدعمه
            'X-Idempotency-Key': `round-${rowKey}-horse-${horseId}-${
              force ? 'force' : 'normal'
            }`,
          };

          return axios.post(`${apiUrl}/Alerts/owner-alerts/notify`, payload, {
            headers,
          });
        };

        // إرسال لكل حصان مع التعامل مع التكرار اليومي (409)
        for (const [horseId, items] of Object.entries(groups)) {
          try {
            await sendOne(horseId, items, false);
            ok++;
          } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data;

            if (status === 409 && data?.duplicate_today) {
              dupFound++;
              const lastTime = data?.last_sent_at
                ? formatKSA(data.last_sent_at)
                : '';
              const confirmMsg = lastTime
                ? `تم إرسال إشعار لهذا الخيل اليوم (${lastTime}). هل تريد إعادة الإرسال؟`
                : 'تم إرسال إشعار لهذا الخيل اليوم. هل تريد إعادة الإرسال؟';

              if (window.confirm(confirmMsg)) {
                try {
                  await sendOne(horseId, items, true);
                  ok++;
                } catch {
                  fail++;
                }
              } else {
                skipped++;
              }
            } else {
              fail++;
            }
          }
        }

        // ملخص
        if (ok && !fail) {
          showSnack(
            `تم الإرسال بنجاح إلى ${ok} خيل${
              skipped ? `، وتخطّينا ${skipped}` : ''
            }${dupFound && !skipped ? ' (تم اكتشاف سجلات مرسلة اليوم)' : ''}`
          );
        } else if (ok && fail) {
          showSnack(
            `أُرسل ${ok} وفشل ${fail}${skipped ? ` وتخطّينا ${skipped}` : ''}`,
            'warning'
          );
        } else {
          showSnack('فشل الإرسال', 'error');
        }
      } catch {
        showSnack('فشل الإرسال', 'error');
      } finally {
        // إزالة حالة الإرسال عن الصف (إعادة تمكين الزر)
        setSending((prev) => {
          const next = new Set(prev);
          next.delete(rowKey);
          return next;
        });
      }
    },
    [showSnack, sending, tplKey]
  );
  useEffect(() => {
    const saved = localStorage.getItem('tplKey');
    if (saved) setTplKey(saved);
  }, []);
  useEffect(() => {
    if (tplKey) localStorage.setItem('tplKey', tplKey);
  }, [tplKey]);

  // ---------- Render ----------
  return (
    <>
      <Box sx={{ p: { xs: 2, md: 3 }, pb: 6 }}>
        {/* Header */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Typography variant="h4" align="center" sx={{ flexGrow: 1 }}>
            {t('vetReport.title', 'تقرير الجولات البيطرية')}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title={t('common.refresh', 'تحديث')}>
              <span>
                <IconButton onClick={loadRounds} disabled={loading}>
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('common.export', 'تصدير JSON')}>
              <span>
                <IconButton
                  onClick={handleExportJSON}
                  disabled={!rounds.length}
                >
                  <Download />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Filters */}
        <Paper sx={{ mb: 2, p: 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth>
                <InputLabel>القالب</InputLabel>
                <Select
                  label="القالب"
                  value={tplKey}
                  onChange={(e) => setTplKey(e.target.value)}
                >
                  {tpls.map((t) => (
                    <MenuItem key={t.template_key} value={t.template_key}>
                      {t.template_key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label={t('common.fromDate', 'من تاريخ')}
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label={t('common.toDate', 'إلى تاريخ')}
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth>
                <InputLabel>
                  {t('accommodationList.stable', 'الإسطبل')}
                </InputLabel>
                <Select
                  label={t('accommodationList.stable', 'الإسطبل')}
                  value={stableId}
                  onChange={(e) => {
                    setStableId(e.target.value);
                    setRoomId('');
                  }}
                >
                  <MenuItem value="">
                    {loadingRooms
                      ? t('common.loading', 'جاري التحميل...')
                      : t('common.all', 'الكل')}
                  </MenuItem>
                  {stables.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth disabled={!stableId}>
                <InputLabel>{t('vetReport.room', 'الغرفة')}</InputLabel>
                <Select
                  label={t('vetReport.room', 'الغرفة')}
                  value={roomId}
                  onChange={(e) => setRoomId(e.target.value)}
                >
                  <MenuItem value="">{t('common.all', 'الكل')}</MenuItem>
                  {roomsOfSelectedStable.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth>
                <InputLabel>{t('vetReport.vet', 'الطبيب')}</InputLabel>
                <Select
                  label={t('vetReport.vet', 'الطبيب')}
                  value={vetId}
                  onChange={(e) => setVetId(e.target.value)}
                >
                  <MenuItem value="">
                    {loadingVets
                      ? t('common.loading', 'جاري التحميل...')
                      : t('common.all', 'الكل')}
                  </MenuItem>
                  {vets.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name || v.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                size="small"
                placeholder={t('vetReport.searchPH', 'بحث (خيل/ملاحظة/حالة)')}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <FilterList fontSize="small" sx={{ mr: 1 }} />
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" onClick={loadRounds}>
                {t('common.apply', 'تطبيق')}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Error banner */}
        {err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {err}
          </Alert>
        )}

        {/* Results */}
        {loading ? (
          <Grid container spacing={1.25}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid item xs={12} key={i}>
                <Skeleton variant="rounded" height={72} />
              </Grid>
            ))}
          </Grid>
        ) : rounds.length === 0 ? (
          <Alert severity="info">
            {t('vetReport.noData', 'لا توجد بيانات للفترة/المرشحات المحددة')}
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell># {/* رقم الصف */}</TableCell>
                  <TableCell>{t('common.date', 'التاريخ')}</TableCell>
                  <TableCell>
                    {t('accommodationList.stable', 'الإسطبل')}
                  </TableCell>
                  <TableCell>{t('vetReport.room', 'الغرفة')}</TableCell>
                  <TableCell>{t('vetReport.horse', 'الخيل')}</TableCell>
                  <TableCell>{t('vetCheckup.severity', 'الشدة')}</TableCell>
                  <TableCell>
                    {t('vetReport.actionRequired', 'يتطلب إجراء')}
                  </TableCell>
                  {/* <TableCell>{t('vetReport.case', 'الحالة')}</TableCell>*/}
                  <TableCell>{t('vetReport.vet', 'الطبيب')}</TableCell>
                  <TableCell>{t('vetReport.notes', 'ملاحظات')}</TableCell>
                  <TableCell>{t('common.actions', 'ابلاغ المالك')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRounds.map((r, idx) => {
                  const items = Array.isArray(r.items) ? r.items : [];
                  const horses = getHorseLabel(items);
                  const worst = worstSeverityOf(items);
                  const needsAction = anyActionRequired(items);
                  return (
                    <TableRow
                      key={r.round_id}
                      hover
                      sx={(theme) => ({
                        cursor: items.length ? 'pointer' : 'default',
                        ...(needsAction
                          ? {
                              backgroundColor: alpha(
                                theme.palette.error.light,
                                0.06
                              ),
                            }
                          : worst && worst.rank >= 3
                          ? {
                              backgroundColor: alpha(
                                theme.palette.warning.light,
                                0.06
                              ),
                            }
                          : {}),
                      })}
                      onClick={() => {
                        if (items.length) {
                          setItemsOfRound({ round: r, items });
                          // تتبع فتح تفاصيل الجولة
                          logAudit({
                            entity_type: 'vet_checkup_round',
                            entity_id: r.round_id,
                            action: 'VIEW',
                          });
                        }
                      }}
                    >
                      {/* الترقيم */}
                      <TableCell>{page * rowsPerPage + idx + 1}</TableCell>

                      <TableCell>{formatDateTime(r.visit_date)}</TableCell>
                      <TableCell>{r.stable_name || r.stable_id}</TableCell>
                      <TableCell>{r.room_name || r.room_id}</TableCell>
                      <TableCell>{horses || '—'}</TableCell>

                      {/* الشدة (أعلى شدة في الجولة) */}
                      <TableCell>
                        {worst ? (
                          <Chip
                            size="small"
                            color={worst.color}
                            label={worst.label}
                          />
                        ) : (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('common.no', 'لا')}
                          />
                        )}
                      </TableCell>

                      {/* يتطلب إجراء */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            needsAction
                              ? t('common.yes', 'نعم')
                              : t('common.no', 'لا')
                          }
                          color={needsAction ? 'error' : 'default'}
                          variant={needsAction ? 'filled' : 'outlined'}
                        />
                      </TableCell>

                      {/* الحالة */}
                      {/*  <TableCell>
                      {items.length
                        ? items
                            .map(
                              (it) =>
                                it.type_name_ar || it.type_name_en || it.type_id
                            )
                            .filter(Boolean)
                            .join(', ')
                        : '—'}
                    </TableCell>
*/}
                      <TableCell>
                        {r.vet_name || r.vet_user_id || '—'}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 320,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {r.notes || '—'}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={
                              !items.length ||
                              sending.has(String(r.round_id ?? r.id))
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              notifyOwnersForRound(r);
                            }}
                          >
                            {t('vetReport.byemail', 'By email')}
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!items.length}
                            onClick={(e) => {
                              e.stopPropagation();
                              sendWhatsAppForRound(r);
                            }}
                          >
                            {t('vetReport.whatsapp', 'by whatsapp')}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={rounds.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage={t('common.rowsPerPage', 'صفوف لكل صفحة')}
            />
          </TableContainer>
        )}

        {/* Items dialog */}
        <Dialog
          open={!!itemsOfRound}
          onClose={() => setItemsOfRound(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {t('vetReport.itemsOfRound', 'عناصر الجولة')} — #
            {itemsOfRound?.round?.round_id}
          </DialogTitle>
          <DialogContent dividers>
            {!itemsOfRound ||
            !Array.isArray(itemsOfRound.items) ||
            itemsOfRound.items.length === 0 ? (
              <Alert severity="info" sx={{ my: 1 }}>
                {t('vetReport.noItems', 'لا توجد عناصر مرتبطة بهذه الجولة')}
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('vetReport.horse', 'الخيل')}</TableCell>
                    <TableCell>{t('vetReport.case', 'الحالة')}</TableCell>
                    <TableCell>{t('vetCheckup.severity', 'الشدة')}</TableCell>
                    <TableCell>
                      {t('vetReport.actionRequired', 'يتطلب إجراء')}
                    </TableCell>
                    <TableCell>{t('vetReport.followUp', 'متابعة')}</TableCell>
                    <TableCell>
                      {t('vetReport.itemNotes', 'ملاحظات الحالة')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemsOfRound.items.map((it) => (
                    <TableRow
                      key={it.item_id || `${it.horse_id}-${it.type_id}`}
                    >
                      <TableCell>{it.item_id || '—'}</TableCell>
                      <TableCell>
                        {it.horse_name ||
                          it.horse_name_ar ||
                          it.horse_name_en ||
                          it.horse ||
                          it.horse_id}
                      </TableCell>
                      <TableCell>
                        {it.type_name_ar || it.type_name_en || it.type_id}
                      </TableCell>
                      <TableCell>{it.severity || 'low'}</TableCell>
                      <TableCell>
                        {Number(it.action_required)
                          ? t('common.yes', 'نعم')
                          : t('common.no', 'لا')}
                      </TableCell>
                      <TableCell>{it.follow_up_date || '—'}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 360,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {it.notes || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          autoHideDuration={snackbar.sev === 'error' ? 5000 : 2500}
        >
          <Alert
            severity={snackbar.sev}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          >
            {snackbar.msg}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}
